/**
 * Gerçek kamera (getUserMedia). Kiosk tablette çalışır; izin verilmezse ekran çalışmaya
 * devam eder ve operatöre tek satırlık sebep gösterilir.
 *
 * Ölçtüğümüz iki şeyi gösteririz, ölçmediğimizi değil: **parlaklık** ve **sabitlik** küçük bir
 * kareden hesaplanır. "Yüz algılandı / gözler açık" tarayıcıda güvenilir ölçülemez (FaceDetector
 * yaygın değil, yeni bağımlılık kuralı var) — kafa kontratını worker doğrular, uymayan foto
 * FAILED olur ve misafire sebebiyle döner.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

type CameraStatus = 'starting' | 'live' | 'denied' | 'unavailable'

const SAMPLE_SIZE = 48
const SAMPLE_INTERVAL_MS = 400
const MIN_BRIGHTNESS = 0.22
const MAX_MOTION = 0.045

/** Görünen kutuya göre 0-1 normalize dikdörtgen (kesilecek bölge). */
export interface Region {
  x0: number
  y0: number
  x1: number
  y1: number
}

/**
 * Görünen bölge → **ham kare** pikselleri. `object-fit: cover` kaynağın kenarlarını kırpıyor,
 * yani ekranda görünen dikdörtgen kaynağın ortasındaki bir parçadır; ölçen de kesen de bu
 * dönüşümü kullanmak zorunda (#31'in dersi: geometri tek yerde).
 */
function sourceRect(
  video: HTMLVideoElement,
  box: DOMRect,
  region: Region,
): { sx: number; sy: number; sw: number; sh: number } {
  const cover = Math.max(box.width / video.videoWidth, box.height / video.videoHeight)
  const visibleW = box.width / cover
  const visibleH = box.height / cover
  const visibleX = (video.videoWidth - visibleW) / 2
  const visibleY = (video.videoHeight - visibleH) / 2
  return {
    sx: visibleX + region.x0 * visibleW,
    sy: visibleY + region.y0 * visibleH,
    sw: (region.x1 - region.x0) * visibleW,
    sh: (region.y1 - region.y0) * visibleH,
  }
}

interface CameraQuality {
  brightEnough: boolean
  steady: boolean
}

export function useCamera(active: boolean) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const sampleRef = useRef<HTMLCanvasElement | null>(null)
  const previousSampleRef = useRef<Uint8ClampedArray | null>(null)
  const [status, setStatus] = useState<CameraStatus>('starting')
  const [quality, setQuality] = useState<CameraQuality>({ brightEnough: false, steady: false })

  useEffect(() => {
    if (!active) return
    let cancelled = false

    void (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setStatus('unavailable')
        return
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 1440 }, height: { ideal: 1440 } },
          audio: false,
        })
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          await videoRef.current.play().catch(() => undefined)
        }
        setStatus('live')
      } catch (error) {
        if (cancelled) return
        const name = error instanceof DOMException ? error.name : ''
        setStatus(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'unavailable')
      }
    })()

    return () => {
      cancelled = true
      streamRef.current?.getTracks().forEach((t) => t.stop())
      streamRef.current = null
      previousSampleRef.current = null
      setStatus('starting')
      setQuality({ brightEnough: false, steady: false })
    }
  }, [active])

  // Parlaklık + hareket: küçük bir karede, saniyede ~2 kez — kioskun CPU'sunu yemez
  useEffect(() => {
    if (!active || status !== 'live') return
    const canvas = (sampleRef.current ??= document.createElement('canvas'))
    canvas.width = SAMPLE_SIZE
    canvas.height = SAMPLE_SIZE
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return

    const timer = setInterval(() => {
      const video = videoRef.current
      if (!video || video.readyState < 2) return
      ctx.drawImage(video, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE)
      const { data } = ctx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE)

      let sum = 0
      for (let i = 0; i < data.length; i += 4) {
        sum += (0.299 * data[i]! + 0.587 * data[i + 1]! + 0.114 * data[i + 2]!) / 255
      }
      const brightness = sum / (data.length / 4)

      const previous = previousSampleRef.current
      let motion = 1
      if (previous && previous.length === data.length) {
        let diff = 0
        for (let i = 0; i < data.length; i += 4) diff += Math.abs(data[i]! - previous[i]!) / 255
        motion = diff / (data.length / 4)
      }
      previousSampleRef.current = new Uint8ClampedArray(data)

      setQuality({ brightEnough: brightness >= MIN_BRIGHTNESS, steady: motion <= MAX_MOTION })
    }, SAMPLE_INTERVAL_MS)

    return () => clearInterval(timer)
  }, [active, status])

  /**
   * Kalite kontrolü için küçük JPEG — **ekranda görünen** alanın istenen bölgesinden.
   *
   * Video `object-fit: cover` ile gösteriliyor: tarayıcı kaynağın kenarlarını kırpıyor. Ham kareyi
   * yollarsak misafirin gördüğü kadraj ile ölçtüğümüz kadraj farklı olur (oval dışındaki yüz
   * "kadraj uygun" sayılırdı). Bu yüzden önce görünen kaynak dikdörtgeni hesaplanır, sonra
   * `region` (görünen kutuya göre 0-1) oradan kesilir.
   */
  const snapshot = useCallback(async (region?: Region, maxSide = 360): Promise<Blob | null> => {
    const video = videoRef.current
    if (!video || video.videoWidth === 0) return null
    const box = video.getBoundingClientRect()
    if (box.width === 0 || box.height === 0) return null

    const { sx, sy, sw, sh } = sourceRect(video, box, region ?? { x0: 0, y0: 0, x1: 1, y1: 1 })
    if (sw < 1 || sh < 1) return null

    const scale = Math.min(1, maxSide / Math.max(sw, sh))
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(sw * scale)
    canvas.height = Math.round(sh * scale)
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height)
    return await new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.8))
  }, [])

  /**
   * Görünen bölgeyi **ham kare** koordinatlarında 0-1 normalize verir — fotoğrafla birlikte
   * sunucuya giden budur (#98). Foto tam kare gönderilmeye devam ediyor; hangi parçasının
   * üretime gideceğini sunucu bu dikdörtgenden biliyor.
   *
   * Neden kioskta hesaplanıyor: `object-fit: cover` kırpması ve kılavuz ovalinin yerleşimi
   * yalnız burada bilinir. Sunucu piksel boyu görmeden doğrulayabilsin diye normalize.
   */
  const regionInSource = useCallback((region: Region): Region | null => {
    const video = videoRef.current
    if (!video || video.videoWidth === 0) return null
    const box = video.getBoundingClientRect()
    if (box.width === 0 || box.height === 0) return null
    const { sx, sy, sw, sh } = sourceRect(video, box, region)
    if (sw < 1 || sh < 1) return null
    return {
      x0: Math.max(0, sx / video.videoWidth),
      y0: Math.max(0, sy / video.videoHeight),
      x1: Math.min(1, (sx + sw) / video.videoWidth),
      y1: Math.min(1, (sy + sh) / video.videoHeight),
    }
  }, [])

  /** Kareyi PNG olarak alır. Önizleme aynalanır, **kayıt aynalanmaz** — misafirin gerçek yüzü. */
  const capture = useCallback(async (): Promise<Blob | null> => {
    const video = videoRef.current
    if (!video || video.videoWidth === 0) return null
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.drawImage(video, 0, 0)
    return await new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/png'))
  }, [])

  return { videoRef, status, quality, capture, snapshot, regionInSource }
}
